import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { payload } from "./envelope";
import type { components } from "./schema";

export type ReminderConfig = components["schemas"]["ReminderConfig"];
export type ReminderEffectiveness = components["schemas"]["ReminderEffectiveness"];

/**
 * Configuração de lembretes do estabelecimento (`hours_before`, ex. `[24, 2]`
 * = 24h e 2h antes). Nunca configurado devolve o padrão `[24, 2]` — a API
 * aplica em memória sem persistir até o primeiro PUT.
 */
export function reminderConfigsQuery() {
  return queryOptions({
    queryKey: ["reminders"] as const,
    queryFn: () => payload(api.GET("/reminder-configs")),
  });
}

/**
 * Efetividade do lembrete no período: a comparação entre quem foi lembrado e
 * quem não foi. Um número sozinho ("85% comparecem") não diria se o lembrete
 * teve alguma coisa a ver com isso.
 */
export function reminderStatsQuery(days = 30) {
  return queryOptions({
    queryKey: ["reminders", "stats", days] as const,
    queryFn: () =>
      payload(api.GET("/reminder-configs/stats", { params: { query: { days } } })),
  });
}

/**
 * Cada valor precisa estar entre 1 e 72, sem duplicados (`422
 * invalid_hours_before` / `duplicate_hours_before`). Lista vazia é válida e
 * DESLIGA os lembretes automáticos — é assim que se desliga, não há flag
 * separada. O disparo automático fica bloqueado enquanto não existir template
 * aprovado — a config salva mesmo assim.
 */
export function updateReminderConfigs(hoursBefore: number[]): Promise<ReminderConfig> {
  return payload(api.PUT("/reminder-configs", { body: { hours_before: hoursBefore } }));
}

/**
 * Dispara um lembrete de teste de verdade, fora do mecanismo periódico:
 * NÃO grava em `sent_reminders`, então não conta nas métricas nem bloqueia
 * o disparo automático real do mesmo agendamento. `hoursBefore` ausente usa
 * o padrão da API (24).
 */
export async function sendTestReminder(
  appointmentId: string,
  hoursBefore?: number,
): Promise<void> {
  await payload(
    api.POST("/reminder-configs/test", {
      body: { appointment_id: appointmentId, hours_before: hoursBefore ?? 24 },
    }),
  );
}
